import "../../site-chrome.css";
import { initSiteChrome } from "../../site-chrome";
import "./style.css";
import { hasAuthenticatedSession, memberCallbackFromLocation } from "./routing";

declare global {
  interface Window {
    turnstile?: {
      render(target: string | HTMLElement, options: Record<string, unknown>): string;
      reset(widgetId?: string): void;
    };
  }
}

const apiOrigin = (import.meta.env.VITE_MEMBER_API_ORIGIN || "https://api.tgr.tw").replace(/\/$/, "");
const membersOrigin = (import.meta.env.VITE_MEMBERS_ORIGIN || "https://members.tgr.tw").replace(/\/$/, "");
const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

const form = document.querySelector<HTMLFormElement>("#login-form");
const email = document.querySelector<HTMLInputElement>("#email");
const newsletter = document.querySelector<HTMLInputElement>("#newsletter");
const topics = document.querySelector<HTMLElement>("#topics");
const submit = document.querySelector<HTMLButtonElement>("#submit");
const status = document.querySelector<HTMLElement>("#status");

let turnstileToken = "";
let widgetId = "";
let turnstileAttempts = 0;

function loadTurnstile(): void {
  if (typeof window.turnstile?.render === "function" || document.querySelector("script[data-tgr-turnstile]")) return;
  if ("turnstile" in window) {
    try { delete window.turnstile; } catch { /* A non-configurable collision will fail closed below. */ }
  }
  const script = document.createElement("script");
  script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  script.async = true;
  script.defer = true;
  script.dataset.tgrTurnstile = "";
  script.addEventListener("error", () => {
    turnstileAttempts = 101;
    renderTurnstile();
  }, { once: true });
  document.head.appendChild(script);
}

export function callbackFromLocation(locationUrl: string): string {
  return memberCallbackFromLocation(locationUrl, membersOrigin);
}

async function redirectAuthenticatedMember(): Promise<boolean> {
  try {
    const response = await fetch(`${apiOrigin}/api/auth/get-session`, {
      credentials: "include",
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(2500),
    });
    if (!response.ok) return false;
    const payload: unknown = await response.json().catch(() => null);
    if (!hasAuthenticatedSession(payload)) return false;
    window.location.replace(callbackFromLocation(window.location.href));
    return true;
  } catch {
    return false;
  }
}

function setStatus(message: string, kind: "info" | "error" | "success" = "info"): void {
  if (!status) return;
  status.textContent = message;
  status.dataset.kind = kind;
}

function renderTurnstile(): void {
  if (!siteKey) {
    if (submit) submit.disabled = true;
    setStatus("安全驗證尚未設定，會員服務目前未啟用。", "error");
    return;
  }
  const turnstile = window.turnstile;
  if (typeof turnstile?.render !== "function") {
    turnstileAttempts += 1;
    if (turnstileAttempts > 100) {
      if (submit) submit.disabled = true;
      setStatus("安全驗證暫時無法載入，請稍後重試。", "error");
      return;
    }
    window.setTimeout(renderTurnstile, 100);
    return;
  }
  widgetId = turnstile.render("#turnstile-widget", {
    sitekey: siteKey,
    action: "member_login",
    theme: "light",
    language: "zh-tw",
    callback: (token: string) => {
      turnstileToken = token;
      setStatus("");
    },
    "expired-callback": () => {
      turnstileToken = "";
      setStatus("安全驗證已過期，請再試一次。", "error");
    },
    "error-callback": () => {
      turnstileToken = "";
      setStatus("安全驗證暫時無法載入。", "error");
    },
  });
}

newsletter?.addEventListener("change", () => {
  if (topics) topics.hidden = !newsletter.checked;
});

form?.addEventListener("submit", async (event) => {
  event.preventDefault();
  if (!email?.validity.valid) {
    email?.focus();
    setStatus("請輸入有效的 Email。", "error");
    return;
  }
  if (!turnstileToken) {
    setStatus("請先完成安全驗證。", "error");
    return;
  }
  if (submit) submit.disabled = true;
  setStatus("正在送出……");
  const selectedTopics = [...document.querySelectorAll<HTMLInputElement>('input[name="topic"]:checked')].map((item) => item.value);
  try {
    const response = await fetch(`${apiOrigin}/api/auth/sign-in/magic-link`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        email: email.value,
        turnstileToken,
        newsletterConsent: Boolean(newsletter?.checked),
        topics: selectedTopics,
        callbackURL: callbackFromLocation(window.location.href),
        newUserCallbackURL: `${membersOrigin}/welcome`,
        errorCallbackURL: `${window.location.origin}/error`,
      }),
    });
    if (!response.ok) throw new Error("REQUEST_FAILED");
    setStatus("若此信箱可使用，我們會寄出登入連結。請檢查收件匣。", "success");
    form.reset();
    if (topics) topics.hidden = true;
  } catch {
    setStatus("目前無法送出，請稍後再試。", "error");
  } finally {
    turnstileToken = "";
    if (typeof window.turnstile?.reset === "function" && widgetId) window.turnstile.reset(widgetId);
    if (submit) submit.disabled = !siteKey;
  }
});

const error = new URL(window.location.href).searchParams.get("error");
if (error) setStatus("登入連結無效或已過期，請重新寄送。", "error");
initSiteChrome({ siteOrigin: "https://www.tgr.tw", membersOrigin });
void redirectAuthenticatedMember().then((redirecting) => {
  if (redirecting) return;
  loadTurnstile();
  renderTurnstile();
});
